import axios, { AxiosInstance } from 'axios';
import { Documento, LetteraIntento, ApiResponse, LoginRequest, LoginResponse, UserProfile, Commessa, Sottocommessa, RapportoIntervento, InvioRapportiResponse } from '../models/types';
import { logService, LogCategory } from './logService';

// Configurazione base dell'API
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Chiave per il localStorage
const TOKEN_KEY = 'pipeline_auth_token';
const USER_KEY = 'pipeline_auth_user';

// Funzioni helper per gestire il token
export const getStoredToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getStoredUser = (): UserProfile | null => {
  const userJson = localStorage.getItem(USER_KEY);
  if (userJson) {
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  }
  return null;
};

export const setStoredAuth = (token: string, user: UserProfile): void => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const clearStoredAuth = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

// Creazione istanza Axios configurata
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Interceptor per aggiungere il token JWT a ogni richiesta
apiClient.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor per gestire errori globali e 401
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Estrai informazioni dettagliate sull'errore
    const url = error.config?.url || 'unknown';
    const method = error.config?.method?.toUpperCase() || 'UNKNOWN';
    const status = error.response?.status;
    const statusText = error.response?.statusText || '';
    
    // Estrai il messaggio di errore dal server (vari formati possibili)
    let serverMessage = '';
    let serverDetails = '';
    let exceptionType = '';
    let stackTrace = '';
    
    if (error.response?.data) {
      const data = error.response.data;
      
      // Formato ApiResponse del backend
      if (data.message) {
        serverMessage = data.message;
      }
      if (data.errors && Array.isArray(data.errors)) {
        serverDetails = data.errors.join(', ');
      }
      
      // Formato errore ASP.NET Web API InternalServerError(Exception)
      // Restituisce: { Message, ExceptionMessage, ExceptionType, StackTrace, InnerException }
      if (data.Message) {
        serverMessage = data.Message;
      }
      if (data.ExceptionMessage) {
        serverDetails = data.ExceptionMessage;
      }
      if (data.ExceptionType) {
        exceptionType = data.ExceptionType;
      }
      if (data.StackTrace) {
        stackTrace = data.StackTrace;
      }
      
      // Gestisci InnerException (pu� contenere dettagli pi� utili)
      if (data.InnerException) {
        const inner = data.InnerException;
        if (inner.ExceptionMessage && !serverDetails.includes(inner.ExceptionMessage)) {
          serverDetails = serverDetails 
            ? `${serverDetails}\nInner: ${inner.ExceptionMessage}`
            : inner.ExceptionMessage;
        }
        if (inner.ExceptionType && !exceptionType) {
          exceptionType = inner.ExceptionType;
        }
        if (inner.StackTrace && !stackTrace) {
          stackTrace = inner.StackTrace;
        }
      }
      
      if (data.ModelState) {
        // Errori di validazione ModelState
        const modelErrors = Object.values(data.ModelState).flat();
        serverDetails = modelErrors.join(', ');
      }
      
      // Se data � una stringa
      if (typeof data === 'string') {
        serverMessage = data;
      }
    }
    
    // Messaggio di errore di rete
    const networkError = !error.response ? error.message || 'Errore di rete' : '';
    
    // Costruisci il messaggio di log
    const logMessage = status 
      ? `${method} ${url} - ${status} ${statusText}`
      : `${method} ${url} - Errore di rete`;
    
    // Costruisci i dettagli con tutte le informazioni disponibili
    const detailsParts = [
      networkError && `Rete: ${networkError}`,
      serverMessage && `Server: ${serverMessage}`,
      serverDetails && `Dettagli: ${serverDetails}`,
      exceptionType && `Tipo: ${exceptionType}`,
      stackTrace && `StackTrace:\n${stackTrace}`,
    ].filter(Boolean);
    
    const details = detailsParts.join('\n');
    
    logService.error(
      LogCategory.API, 
      logMessage,
      details || error
    );
    
    // Se ricevo 401 Unauthorized, pulisco l'auth e redirect al login
    // Ma NON se siamo gi� sulla pagina di login o se � una richiesta di login
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      const isOnLoginPage = window.location.pathname === '/login';
      
      if (!isLoginRequest && !isOnLoginPage) {
        logService.warn(LogCategory.AUTH, 'Sessione scaduta, redirect al login');
        clearStoredAuth();
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Servizio per l'Autenticazione
export const authService = {
  login: async (credentials: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/login', credentials);
    
    // Se login riuscito, salva token e user
    if (response.data.success && response.data.data) {
      setStoredAuth(response.data.data.token, response.data.data.user);
    }
    
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      clearStoredAuth();
    }
  },

  getProfile: async (): Promise<ApiResponse<UserProfile>> => {
    const response = await apiClient.get<ApiResponse<UserProfile>>('/auth/profile');
    return response.data;
  },

  verifyToken: async (): Promise<boolean> => {
    try {
      const response = await apiClient.get<ApiResponse<{ valid: boolean }>>('/auth/verify');
      return response.data.success && response.data.data?.valid === true;
    } catch {
      return false;
    }
  },

  refreshToken: async (): Promise<ApiResponse<LoginResponse>> => {
    const response = await apiClient.post<ApiResponse<LoginResponse>>('/auth/refresh');
    
    // Se refresh riuscito, aggiorna token e user
    if (response.data.success && response.data.data) {
      setStoredAuth(response.data.data.token, response.data.data.user);
    }
    
    return response.data;
  },

  isAuthenticated: (): boolean => {
    return !!getStoredToken();
  },

  getCurrentUser: (): UserProfile | null => {
    return getStoredUser();
  },
};

// Servizio per i Documenti
export const documentoService = {
  getAll: async (): Promise<Documento[]> => {
    const response = await apiClient.get<Documento[]>('/documenti');
    return response.data;
  },

  getById: async (id: number): Promise<Documento> => {
    const response = await apiClient.get<Documento>(`/documenti/${id}`);
    return response.data;
  },

  create: async (documento: Partial<Documento>): Promise<ApiResponse<Documento>> => {
    const response = await apiClient.post<ApiResponse<Documento>>('/documenti', documento);
    return response.data;
  },

  update: async (id: number, documento: Partial<Documento>): Promise<ApiResponse<Documento>> => {
    const response = await apiClient.put<ApiResponse<Documento>>(`/documenti/${id}`, documento);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete<ApiResponse<void>>(`/documenti/${id}`);
    return response.data;
  },
};

// Servizio per le Lettere d'Intento
export const letteraIntentoService = {
  getAll: async (): Promise<LetteraIntento[]> => {
    const response = await apiClient.get<LetteraIntento[]>('/lettere-intento');
    return response.data;
  },

  getById: async (id: number): Promise<LetteraIntento> => {
    const response = await apiClient.get<LetteraIntento>(`/lettere-intento/${id}`);
    return response.data;
  },

  create: async (lettera: Partial<LetteraIntento>): Promise<ApiResponse<LetteraIntento>> => {
    const response = await apiClient.post<ApiResponse<LetteraIntento>>('/lettere-intento', lettera);
    return response.data;
  },

  update: async (id: number, lettera: Partial<LetteraIntento>): Promise<ApiResponse<LetteraIntento>> => {
    const response = await apiClient.put<ApiResponse<LetteraIntento>>(`/lettere-intento/${id}`, lettera);
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete<ApiResponse<void>>(`/lettere-intento/${id}`);
    return response.data;
  },
};

// Servizio per le Commesse
export const commessaService = {
  /**
   * Ottiene le commesse aperte con ricerca opzionale
   */
  getCommesseAperte: async (search?: string): Promise<Commessa[]> => {
    const params = search ? { search } : {};
    const response = await apiClient.get<ApiResponse<Commessa[]>>('/commesse', { params });
    return response.data.data || [];
  },

  /**
   * Ottiene tutte le commesse con filtri
   */
  getTutte: async (search?: string, includiChiuse: boolean = false): Promise<Commessa[]> => {
    const response = await apiClient.get<ApiResponse<Commessa[]>>('/commesse/tutte', {
      params: { search, includiChiuse }
    });
    return response.data.data || [];
  },

  /**
   * Ottiene una commessa per ID
   */
  getById: async (id: number): Promise<Commessa | null> => {
    try {
      const response = await apiClient.get<ApiResponse<Commessa>>(`/commesse/${id}`);
      return response.data.data || null;
    } catch {
      return null;
    }
  },
};

// Servizio per le Sottocommesse
export const sottocommessaService = {
  /**
   * Ottiene le sottocommesse di una commessa con ricerca opzionale
   */
  getByCommessa: async (commessaId: number, search?: string): Promise<Sottocommessa[]> => {
    const response = await apiClient.get<ApiResponse<Sottocommessa[]>>('/sottocommesse', {
      params: { commessaId, search }
    });
    return response.data.data || [];
  },

  /**
   * Ottiene una sottocommessa per ID
   */
  getById: async (id: number): Promise<Sottocommessa | null> => {
    try {
      const response = await apiClient.get<ApiResponse<Sottocommessa>>(`/sottocommesse/${id}`);
      return response.data.data || null;
    } catch {
      return null;
    }
  },
};

// Servizio per i Rapporti Ore
export const rapportiOreService = {
  /**
   * Invia rapporti ore al backend (batch)
   */
  inviaRapporti: async (rapporti: RapportoIntervento[]): Promise<InvioRapportiResponse> => {
    const rapportiDaInviare = rapporti.map(r => ({
      id: r.id,
      operaioId: r.operaioId,
      operaioNome: r.operaioNome,
      data: r.data,
      ore: r.ore,
      descrizione: r.descrizione,
      commessaId: r.commessaId,
      commessaTitolo: r.commessaTitolo,
      sottocommessaId: r.sottocommessaId,
      sottocommessaNome: r.sottocommessaNome,
      createdAt: r.createdAt,
      updatedAt: r.updatedAt,
      allegati: r.allegati?.map(a => ({
        id: a.id,
        dataUri: a.dataUri,
        descrizione: a.descrizione,
        tipoAllegatoId: a.tipoAllegatoId,
        tipoAllegatoNome: a.tipoAllegatoNome,
        createdAt: a.createdAt,
      })) || [],
    }));

    const response = await apiClient.post<ApiResponse<InvioRapportiResponse>>('/rapporti-ore/invio', {
      rapporti: rapportiDaInviare,
    });
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Errore durante l\'invio dei rapporti');
    }
    
    return response.data.data;
  },

  /**
   * Invia un singolo rapporto ore al backend
   */
  inviaRapporto: async (rapporto: RapportoIntervento): Promise<ApiResponse<unknown>> => {
    const rapportoDaInviare = {
      id: rapporto.id,
      operaioId: rapporto.operaioId,
      operaioNome: rapporto.operaioNome,
      data: rapporto.data,
      ore: rapporto.ore,
      descrizione: rapporto.descrizione,
      commessaId: rapporto.commessaId,
      commessaTitolo: rapporto.commessaTitolo,
      sottocommessaId: rapporto.sottocommessaId,
      sottocommessaNome: rapporto.sottocommessaNome,
      createdAt: rapporto.createdAt,
      updatedAt: rapporto.updatedAt,
      allegati: rapporto.allegati?.map(a => ({
        id: a.id,
        dataUri: a.dataUri,
        descrizione: a.descrizione,
        tipoAllegatoId: a.tipoAllegatoId,
        tipoAllegatoNome: a.tipoAllegatoNome,
        createdAt: a.createdAt,
      })) || [],
    };

    const response = await apiClient.post<ApiResponse<unknown>>('/rapporti-ore', rapportoDaInviare);
    return response.data;
  },

  /**
   * Verifica quali rapporti sono gi� sincronizzati
   */
  verificaSincronizzazione: async (idLocali: string[]): Promise<Record<string, boolean>> => {
    const response = await apiClient.post<ApiResponse<Record<string, boolean>>>('/rapporti-ore/verifica', idLocali);
    return response.data.data || {};
  },

  /**
   * Ottiene le ore dell'ultima settimana confermate in consuntivo
   */
  getUltimaSettimanaConfermati: async (operaioId?: number, commessaId?: number): Promise<ApiResponse<any[]>> => {
    const params: any = {};
    if (operaioId) params.operaioId = operaioId;
    if (commessaId) params.commessaId = commessaId;

    const response = await apiClient.get<ApiResponse<any[]>>('/rapporti-ore/ultima-settimana-confermati', { params });
    return response.data;
  },
};

export default apiClient;
